import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Package, Grid3x3, Mail, Handshake, Users, Image, Calendar, TrendingUp, Clock } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { getDashboardStats } from '../../services/api';

const Dashboard = () => {
  const { user } = useOutletContext();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await getDashboardStats();
        setStats(res.data);
      } catch (err) {
        console.error('Failed to load stats');
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  const statCards = stats ? [
    { icon: Package, label: 'Products', value: stats.total_products, color: 'bg-blue-500' },
    { icon: Grid3x3, label: 'Categories', value: stats.total_categories, color: 'bg-indigo-500' },
    { icon: Users, label: 'Clients', value: stats.total_clients, color: 'bg-teal-500' },
    { icon: Image, label: 'Banners', value: stats.total_banners, color: 'bg-amber-500' },
    { icon: Calendar, label: 'Events', value: stats.total_events, color: 'bg-purple-500' },
    { icon: Mail, label: 'Enquiries', value: stats.total_enquiries, color: 'bg-green-500', badge: stats.pending_enquiries > 0 ? `${stats.pending_enquiries} new` : null },
    { icon: Handshake, label: 'Dealerships', value: stats.total_dealerships, color: 'bg-orange-500', badge: stats.pending_dealerships > 0 ? `${stats.pending_dealerships} pending` : null },
  ] : [];

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Welcome back, <span className="text-[#2E3A6F]">{user?.username}</span>
        </h1>
        <p className="text-sm text-gray-500 mt-1 capitalize">
          Role: {user?.role?.replace('_', ' ')} &middot; {new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </p>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[...Array(7)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-5">
                <div className="h-12 bg-gray-200 rounded" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {statCards.map((stat, i) => (
            <Card key={i} className="border-0 shadow-sm hover:shadow-md transition-shadow" data-testid={`stat-${stat.label.toLowerCase()}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">{stat.label}</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                    {stat.badge && (
                      <span className="inline-block mt-2 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-600">
                        {stat.badge}
                      </span>
                    )}
                  </div>
                  <div className={`w-10 h-10 rounded-lg ${stat.color} flex items-center justify-center`}>
                    <stat.icon className="text-white" size={20} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <TrendingUp size={18} className="text-[#2E3A6F]" />
              Quick Actions
            </h3>
            <div className="space-y-2">
              {[
                { label: 'Add New Product', path: '/admin/products', color: 'bg-blue-50 text-blue-700' },
                { label: 'Manage Categories', path: '/admin/categories', color: 'bg-indigo-50 text-indigo-700' },
                { label: 'View Enquiries', path: '/admin/enquiries', color: 'bg-green-50 text-green-700' },
                { label: 'Add Client Logo', path: '/admin/clients', color: 'bg-teal-50 text-teal-700' },
              ].map((action, i) => (
                <a
                  key={i}
                  href={action.path}
                  className={`block px-4 py-3 rounded-lg ${action.color} text-sm font-medium hover:opacity-80 transition-opacity`}
                >
                  {action.label}
                </a>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="p-6">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
              <Clock size={18} className="text-[#2E3A6F]" />
              System Info
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-500">Logged in as</span>
                <span className="font-medium">{user?.username}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-500">Role</span>
                <span className="font-medium capitalize">{user?.role?.replace('_', ' ')}</span>
              </div>
              <div className="flex justify-between py-2 border-b border-gray-100">
                <span className="text-gray-500">Version</span>
                <span className="font-medium">1.0.0</span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-500">Status</span>
                <span className="inline-flex items-center gap-1 text-green-600 font-medium">
                  <span className="w-2 h-2 rounded-full bg-green-500" /> Online
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
